import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { useUIStore } from '@/store/uiStore'
import { useAuth } from '@/hooks/useAuth'

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordModal() {
  const { activeModal, closeModal, openModal } = useUIStore()
  const { resetPassword } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true)
    try {
      await resetPassword(data.email)
      setSent(true)
    } catch {
      // Error handled in useAuth
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    closeModal()
    setSent(false)
    reset()
  }

  const switchToLogin = () => {
    handleClose()
    setTimeout(() => openModal('login'), 100)
  }

  return (
    <Modal isOpen={activeModal === 'forgot-password'} onClose={handleClose} title="Reset Password">
      {sent ? (
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <span className="text-3xl">✉️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">Check your email</h3>
          <p className="text-slate-600 mb-6">
            We've sent a password reset link to your email address. Follow the instructions to reset your password.
          </p>
          <Button onClick={switchToLogin} fullWidth>
            Back to Login
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-slate-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            error={errors.email?.message}
            {...register('email')}
          />

          <Button type="submit" fullWidth loading={isSubmitting}>
            Send Reset Link
          </Button>

          <p className="text-center text-sm text-slate-600">
            Remember your password?{' '}
            <button type="button" onClick={switchToLogin} className="link">
              Log in
            </button>
          </p>
        </form>
      )}
    </Modal>
  )
}
